import React from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Table,
  TableProps,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material"

import TruncatedText from "components/ui/TruncatedText"
import { colors } from "theme"
import { formatDate, formatMoney } from "helpers/formatters"
import {
  DATE_TYPE,
  MONEY_TYPE,
  STRING_TYPE,
  STRING_STRONG_TYPE,
  STRING_CENTER_TYPE,
  STRING_TRUNCATED_TYPE,
  LINK_TYPE,
} from "config"

type Props = TableProps & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  header: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  footer?: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[]
  fade: boolean
}

type Formats =
  | typeof DATE_TYPE
  | typeof MONEY_TYPE
  | typeof STRING_TYPE
  | typeof STRING_STRONG_TYPE
  | typeof STRING_CENTER_TYPE
  | typeof STRING_TRUNCATED_TYPE
  | typeof LINK_TYPE

const TableComponent = ({
  header,
  footer,
  items,
  fade,
  ...restProps
}: Props) => {
  const { t } = useTranslation()

  const formatValue = (format: Formats, value: string): React.ReactNode => {
    switch (format) {
      case DATE_TYPE:
        return <>{formatDate(value)}</>
      case MONEY_TYPE:
        return <>{formatMoney(value)}</>
      case STRING_TYPE:
        return <>{value}</>
      case STRING_CENTER_TYPE:
        return <>{value}</>
      case STRING_TRUNCATED_TYPE:
        return <TruncatedText fullText={value} />
      case STRING_STRONG_TYPE:
        return <strong>{value}</strong>
      case LINK_TYPE:
        return (
          <ActionLink target="_blank" to={value}>
            {t("table.actions.download")}
          </ActionLink>
        )
      default:
        throw new Error(`Unknown format ${format}`)
    }
  }

  const alignment = (format: Formats): TableCellProps["align"] => {
    switch (format) {
      case DATE_TYPE:
      case STRING_TYPE:
      case STRING_TRUNCATED_TYPE:
        return "left"
      case STRING_CENTER_TYPE:
        return "center"
      case STRING_STRONG_TYPE:
        return "left"
      case LINK_TYPE:
        return "center"
      case MONEY_TYPE:
        return "right"
      default:
        throw new Error(`Unknown format ${format}`)
    }
  }

  return (
    <TableContainer {...restProps}>
      <Table stickyHeader aria-label="sticky table">
        <THead>
          <TableRow>
            {header.map(({ label, format, minWidth }, headerIndex) => (
              <TableCell
                key={`header-${headerIndex}`}
                align={alignment(format)}
                style={{ minWidth: minWidth }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </THead>
        <TBody>
          {items.map((item, rowIndex) => (
            <TableRow
              key={`row-${rowIndex}`}
              hover
              role="checkbox"
              tabIndex={-1}
            >
              {header.map(({ field, format }, cellIndex) => (
                <TableCell
                  key={`row-${rowIndex}-cell-${cellIndex}`}
                  align={alignment(format)}
                >
                  {formatValue(format, item[field])}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {footer && (
            <>
              <TableRow key="footer" hover role="checkbox" tabIndex={-1}>
                {footer.map(({ format, value }, cellIndex) => (
                  <TableCell
                    key={`row-footer-cell-${cellIndex}`}
                    align={alignment(format)}
                  >
                    {format === MONEY_TYPE && <>{formatMoney(value)}</>}
                    {format === STRING_TYPE && <strong>{value}</strong>}
                  </TableCell>
                ))}
              </TableRow>
            </>
          )}
          {fade && <TBodyFade />}
        </TBody>
      </Table>
    </TableContainer>
  )
}

TableComponent.defaultProps = {
  fade: false,
}

export default TableComponent

const THead = styled(TableHead)`
  &.MuiTableHead-root {
    tr {
      background-color: ${colors.antiFlashWhite};
    }

    th {
      background-color: ${colors.antiFlashWhite};
      border-bottom: none;
      font-size: 0.75rem;
      line-height: 16px;
      padding: 10px 5px;
    }

    th:first-of-type {
      border-radius: 10px 0 0 10px;
      padding-left: 10px;
    }

    th:last-child {
      border-radius: 0 10px 10px 0;
      padding-right: 10px;
    }
  }
`
const TBody = styled(TableBody)`
  &.MuiTableBody-root {
    position: relative;
    tr {
      &:hover {
        background-color: transparent;
      }
    }

    td {
      border-color: ${colors.antiFlashWhite};
      font-size: 0.75rem;
      font-weight: 400;
      line-height: 16px;
      padding: 10px 5px;
    }

    td div {
      font-size: 0.75rem;
    }

    td:first-of-type {
      padding-left: 10px;
    }

    td:last-child {
      padding-right: 10px;
    }
  }
`
const TBodyFade = styled(TableRow)`
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100%;
    right: 0;
    background: linear-gradient(0deg, #ffffff 0%, rgba(255, 255, 255, 0.15) 5%);
  }
`

const ActionLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "0.875rem",
  fontWeight: "500",
  lineHeight: "15px",
  margin: "0 32px",
  textDecoration: "none",
  [theme.breakpoints.down("md")]: {
    margin: "0",
  },
}))
